import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import MapView from "./MapView";
import ElevationProfile from "./ElevationProfile";
import MediaPlayer from "./MediaPlayer";
import { Switch } from "./ui/switch";
import { SpinnerGap } from "@phosphor-icons/react";
import { toast } from "sonner";

const clamp = (val, min, max) => Math.min(max, Math.max(min, val));
const roundToTenth = (val) => Math.round((Number(val) || 0) * 10) / 10;

function normalizeSceneTimes(sceneTimes = []) {
  if (!Array.isArray(sceneTimes)) return [];
  return sceneTimes
    .map((t) => Number(t))
    .filter((t) => Number.isFinite(t) && t >= 0)
    .sort((a, b) => a - b);
}

function findClosestSceneTime(sceneTimes = [], seconds = 0, bounds = {}) {
  if (!Array.isArray(sceneTimes) || sceneTimes.length === 0) return null;

  const target = Number(seconds) || 0;
  const min = Number.isFinite(Number(bounds?.min)) ? Number(bounds.min) : null;
  const max = Number.isFinite(Number(bounds?.max)) ? Number(bounds.max) : null;

  let best = null;
  let bestDistance = Infinity;
  for (const sceneTime of sceneTimes) {
    if (min != null && sceneTime < min) continue;
    if (max != null && sceneTime > max) continue;
    const distance = Math.abs(sceneTime - target);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = sceneTime;
    }
  }

  if (best == null) return null;
  return roundToTenth(best);
}

function getRoutePointsByID(routes = [], editingRoute, routeID = "") {
  if (editingRoute) {
    return editingRoute.gpx_points;
  }
  const route = (routes || []).find((r) => r.id === routeID);
  return route?.gpx_points || route?.gpxPoints || [];
}

function getDefaultMarkerRoutePoints(
  videoDraft = {},
  routes = [],
  editingRoute = {},
  editingPlaylist = {},
) {
  if (
    Array.isArray(videoDraft?.gpx_points) &&
    videoDraft.gpx_points.length > 0
  ) {
    return videoDraft.gpx_points;
  }
  if (Array.isArray(videoDraft?.gpxPoints) && videoDraft.gpxPoints.length > 0) {
    return videoDraft.gpxPoints;
  }
  if (
    Array.isArray(editingPlaylist?.gpx_points) &&
    editingPlaylist.gpx_points.length > 0
  ) {
    return editingPlaylist.gpx_points;
  }
  if (
    Array.isArray(editingPlaylist?.gpxPoints) &&
    editingPlaylist.gpxPoints.length > 0
  ) {
    return editingPlaylist.gpxPoints;
  }
  return getRoutePointsByID(routes, editingRoute, videoDraft?.route_id);
}

function getPointAtTime(gpxPoints = [], seconds = 0, duration = 0) {
  if (!Array.isArray(gpxPoints) || gpxPoints.length === 0) return null;
  if (gpxPoints.length === 1) {
    return [Number(gpxPoints[0][0]) || 0, Number(gpxPoints[0][1]) || 0];
  }

  const total = Number(duration) || 0;
  const t = Number(seconds) || 0;
  const progress = total > 0 ? clamp(t / total, 0, 1) : 0;
  const idx = Math.round(progress * (gpxPoints.length - 1));
  const p = gpxPoints[idx] || gpxPoints[0];
  return [Number(p[0]) || 0, Number(p[1]) || 0];
}

function buildDefaultMarker(
  videoDraft = {},
  routes = [],
  editingRoute = {},
  editingPlaylist = {},
) {
  const routePoints = getDefaultMarkerRoutePoints(
    videoDraft,
    routes,
    editingRoute,
    editingPlaylist,
  );
  const first = routePoints[0];
  const last = routePoints[routePoints.length - 1] || first;
  const startLat = Number(first?.[0]) || 0;
  const startLng = Number(first?.[1]) || 0;
  const endLat = Number(last?.[0]) || startLat;
  const endLng = Number(last?.[1]) || startLng;
  const duration = Math.max(0, Number(videoDraft?.duration) || 0);

  return {
    from_sec: 0,
    to_sec: roundToTenth(duration),
    start_lat: startLat,
    start_lng: startLng,
    end_lat: endLat,
    end_lng: endLng,
    radius_meters: null,
  };
}

function ensureMarkers(
  videoDraft = {},
  routes = [],
  editingRoute = {},
  editingPlaylist = {},
) {
  const existing = Array.isArray(videoDraft?.markers) ? videoDraft.markers : [];
  if (existing.length > 0) return existing;
  return [
    buildDefaultMarker(videoDraft, routes, editingRoute, editingPlaylist),
  ];
}

function interpolateAndFillMarkerRanges(
  markers = [],
  totalDuration = 0,
  gpxPoints = [],
) {
  const duration = roundToTenth(Math.max(0, Number(totalDuration) || 0));
  const pointAt = (t) => getPointAtTime(gpxPoints, t, duration) || [0, 0];
  const hasCoord = (lat, lng) =>
    Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));

  const normalized = (markers || [])
    .map((m) => {
      const from = roundToTenth(clamp(Number(m?.from_sec) || 0, 0, duration));
      const to = roundToTenth(
        clamp(Math.max(from, Number(m?.to_sec) || from), 0, duration),
      );
      const isStatic =
        m?.radius_meters !== null && m?.radius_meters !== undefined;

      const defaultStart = pointAt(from);
      const defaultEnd = pointAt(to);
      const start = hasCoord(m?.start_lat, m?.start_lng)
        ? [Number(m.start_lat), Number(m.start_lng)]
        : defaultStart;
      const end = isStatic
        ? start
        : hasCoord(m?.end_lat, m?.end_lng)
          ? [Number(m.end_lat), Number(m.end_lng)]
          : defaultEnd;

      return {
        ...m,
        from_sec: from,
        to_sec: to,
        start_lat: start[0],
        start_lng: start[1],
        end_lat: end[0],
        end_lng: end[1],
        radius_meters: isStatic
          ? Math.max(0, Number(m.radius_meters) || 0)
          : null,
      };
    })
    .sort((a, b) => a.from_sec - b.from_sec || a.to_sec - b.to_sec);

  const filled = [];
  let cursor = 0;

  const pushFiller = (from, to) => {
    if (to <= from) return;
    const start = pointAt(from);
    const end = pointAt(to);
    filled.push({
      from_sec: roundToTenth(from),
      to_sec: roundToTenth(to),
      start_lat: start[0],
      start_lng: start[1],
      end_lat: end[0],
      end_lng: end[1],
      radius_meters: null,
    });
  };

  for (const marker of normalized) {
    const from = roundToTenth(Math.max(cursor, marker.from_sec));
    const to = roundToTenth(Math.max(from, marker.to_sec));

    if (from > cursor) {
      pushFiller(cursor, from);
    }

    const startInterpolated = pointAt(from);
    const endInterpolated = pointAt(to);
    const isStatic =
      marker.radius_meters !== null && marker.radius_meters !== undefined;

    const start =
      marker.from_sec === from
        ? [marker.start_lat, marker.start_lng]
        : startInterpolated;
    const end = isStatic
      ? start
      : marker.to_sec === to
        ? [marker.end_lat, marker.end_lng]
        : endInterpolated;

    filled.push({
      ...marker,
      from_sec: from,
      to_sec: to,
      start_lat: start[0],
      start_lng: start[1],
      end_lat: end[0],
      end_lng: end[1],
      radius_meters: isStatic ? marker.radius_meters : null,
    });

    cursor = roundToTenth(Math.max(cursor, to));
  }

  if (cursor < duration) {
    pushFiller(cursor, duration);
  }

  if (filled.length === 0) {
    const start = pointAt(0);
    const end = pointAt(duration);
    return [
      {
        from_sec: 0,
        to_sec: duration,
        start_lat: start[0],
        start_lng: start[1],
        end_lat: end[0],
        end_lng: end[1],
        radius_meters: null,
      },
    ];
  }

  return filled;
}

export default function MarkersEditorShared({
  video,
  routes = [],
  editingRoute = {},
  editingPlaylist = {},
  onClose,
  onSaved,
  onSave,
}) {
  const [markerDraft, setMarkerDraft] = useState([]);
  const [markerCurrentTime, setMarkerCurrentTime] = useState(0);
  const [markerDuration, setMarkerDuration] = useState(0);
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState(null);
  const [isMarkerSelectionLocked, setIsMarkerSelectionLocked] = useState(true);
  const [snapToSceneChanges, setSnapToSceneChanges] = useState(true);
  const [staticMarkerLengthSec, setStaticMarkerLengthSec] = useState(10);
  const [staticMarkerRadiusMeters, setStaticMarkerRadiusMeters] = useState(100);
  const [mapPickMode, setMapPickMode] = useState(null);
  const [hoveredRouteTime, setHoveredRouteTime] = useState(null);
  const markerPlayerRef = useRef(null);

  const sceneTimes = useMemo(
    () => normalizeSceneTimes(video?.scene_times || video?.sceneTimes || []),
    [video?.scene_times, video?.sceneTimes],
  );
  const shouldSnapToSceneChanges = snapToSceneChanges && sceneTimes.length > 0;

  const markerRoutePoints = useMemo(() => {
    return getDefaultMarkerRoutePoints(
      video,
      routes,
      editingRoute,
      editingPlaylist,
    );
  }, [video, routes, editingRoute, editingPlaylist]);

  useEffect(() => {
    if (!video) {
      setMarkerDraft([]);
      setMarkerCurrentTime(0);
      setMarkerDuration(0);
      setSelectedMarkerIndex(null);
      setIsMarkerSelectionLocked(true);
      setStaticMarkerLengthSec(10);
      setStaticMarkerRadiusMeters(100);
      setMapPickMode(null);
      setHoveredRouteTime(null);
      return;
    }
    setMarkerCurrentTime(0);
    setMarkerDuration(Number(video.duration) || 0);
    setSelectedMarkerIndex(0);
    setIsMarkerSelectionLocked(true);
    setSnapToSceneChanges(true);
    setStaticMarkerLengthSec(10);
    setStaticMarkerRadiusMeters(100);
    setMapPickMode(null);
    setHoveredRouteTime(null);
    setMarkerDraft(ensureMarkers(video, routes, editingRoute, editingPlaylist));
  }, [video, routes]);

  const markerSectionLines = useMemo(() => {
    if (!Array.isArray(markerDraft) || markerDraft.length === 0) return [];
    return markerDraft
      .map((m) => Number(m?.from_sec))
      .filter((t) => Number.isFinite(t) && t > 0)
      .sort((a, b) => a - b);
  }, [markerDraft]);

  const elevationSeries = useMemo(() => {
    const totalDuration = Math.max(
      0,
      Number(markerDuration) || Number(video?.duration) || 0,
    );

    const seriesFromElevation = (markerRoutePoints || [])
      .map((p, i) => {
        if (!Array.isArray(p) || p.length < 3) return null;
        const t =
          totalDuration > 0
            ? (i / Math.max(1, markerRoutePoints.length - 1)) * totalDuration
            : i;
        const ele = Number(p[2]);
        if (!Number.isFinite(ele)) return null;
        return { t, ele, idx: i };
      })
      .filter(Boolean);

    if (seriesFromElevation.length > 0) return seriesFromElevation;

    const fallbackTimes = (markerDraft || [])
      .flatMap((m) => [Number(m?.from_sec), Number(m?.to_sec)])
      .filter((t) => Number.isFinite(t));

    if (totalDuration > 0) fallbackTimes.push(0, totalDuration);

    const uniqueSortedTimes = Array.from(new Set(fallbackTimes)).sort(
      (a, b) => a - b,
    );

    return uniqueSortedTimes.map((t, idx) => ({
      t,
      ele: 0,
      idx,
    }));
  }, [markerDraft, markerDuration, markerRoutePoints, video?.duration]);

  const hoveredRoutePosition = useMemo(() => {
    const hoverTime = Number(hoveredRouteTime);
    const totalDuration = Math.max(
      0,
      Number(markerDuration) || Number(video?.duration) || 0,
    );

    if (
      !Number.isFinite(hoverTime) ||
      hoverTime < 0 ||
      !Array.isArray(markerRoutePoints) ||
      markerRoutePoints.length === 0 ||
      totalDuration <= 0
    ) {
      return null;
    }

    const ratio = Math.min(1, Math.max(0, hoverTime / totalDuration));
    const idx = Math.min(
      markerRoutePoints.length - 1,
      Math.max(
        0,
        Math.round(ratio * Math.max(1, markerRoutePoints.length - 1)),
      ),
    );
    const point = markerRoutePoints[idx];
    if (!Array.isArray(point) || point.length < 2) return null;
    return [Number(point[0]) || 0, Number(point[1]) || 0];
  }, [hoveredRouteTime, markerDuration, markerRoutePoints, video?.duration]);

  const handleProfileSeek = (seconds) => {
    const nextTime = Number(seconds);
    if (!Number.isFinite(nextTime)) return;
    markerPlayerRef.current?.seekTo?.(nextTime);
    setMarkerCurrentTime(nextTime);
  };

  const handleHoverTime = (seconds) => {
    if (seconds == null || !Number.isFinite(Number(seconds))) {
      setHoveredRouteTime(null);
      return;
    }
    setHoveredRouteTime(Number(seconds));
  };

  useEffect(() => {
    if (!isMarkerSelectionLocked || mapPickMode || markerDraft.length === 0)
      return;

    const now = Number(markerCurrentTime) || 0;
    const activeIdx = markerDraft.findIndex((m) => {
      const from = roundToTenth(Number(m?.from_sec) || 0);
      const to = roundToTenth(Math.max(from, Number(m?.to_sec) || from));
      return now >= from && now <= to;
    });

    if (activeIdx >= 0 && activeIdx !== selectedMarkerIndex) {
      setSelectedMarkerIndex(activeIdx);
    }
  }, [
    isMarkerSelectionLocked,
    mapPickMode,
    markerCurrentTime,
    markerDraft,
    selectedMarkerIndex,
  ]);

  const closestGpxPoint = (lat, lng) => {
    if (!Array.isArray(markerRoutePoints) || markerRoutePoints.length === 0)
      return null;
    let best = null;
    let bestDist = Infinity;
    for (const p of markerRoutePoints) {
      if (!Array.isArray(p) || p.length < 2) continue;
      const dLat = (Number(p[0]) || 0) - lat;
      const dLng = (Number(p[1]) || 0) - lng;
      const d = dLat * dLat + dLng * dLng;
      if (d < bestDist) {
        bestDist = d;
        best = [Number(p[0]) || 0, Number(p[1]) || 0];
      }
    }
    return best;
  };

  const splitSelectedMarkerAtCurrentTime = () => {
    if (selectedMarkerIndex == null || !markerDraft[selectedMarkerIndex])
      return;
    const target = markerDraft[selectedMarkerIndex];
    const from = roundToTenth(Number(target.from_sec) || 0);
    const to = roundToTenth(Math.max(from, Number(target.to_sec) || from));
    const rawSplitTime = roundToTenth(
      clamp(Number(markerCurrentTime) || 0, from, to),
    );
    const splitTime = shouldSnapToSceneChanges
      ? (findClosestSceneTime(sceneTimes, rawSplitTime, {
          min: from,
          max: to,
        }) ?? rawSplitTime)
      : rawSplitTime;

    if (splitTime <= from || splitTime >= to) {
      toast.error("Move playback inside the marker range before splitting");
      return;
    }

    const splitPoint = getPointAtTime(
      markerRoutePoints,
      splitTime,
      markerDuration || video?.duration,
    ) || [Number(target.end_lat) || 0, Number(target.end_lng) || 0];

    const left = {
      ...target,
      to_sec: roundToTenth(splitTime),
      end_lat: splitPoint[0],
      end_lng: splitPoint[1],
    };
    const right = {
      ...target,
      from_sec: roundToTenth(splitTime),
      start_lat: splitPoint[0],
      start_lng: splitPoint[1],
    };

    const next = [...markerDraft];
    next.splice(selectedMarkerIndex, 1, left, right);
    setMarkerDraft(next);
    setSelectedMarkerIndex(selectedMarkerIndex + 1);
    setMapPickMode({ type: "split", rightIndex: selectedMarkerIndex + 1 });
    toast.success(
      shouldSnapToSceneChanges
        ? "Marker split at nearest scene change"
        : "Marker split at current timestamp",
    );
  };

  const addStaticMarkerAtCurrentTime = () => {
    if (!markerDraft.length) return;

    const total = roundToTenth(
      Math.max(0, Number(markerDuration) || Number(video?.duration) || 0),
    );
    const lengthSec = Math.max(0, Number(staticMarkerLengthSec) || 0);
    const radiusMeters = Math.max(0, Number(staticMarkerRadiusMeters) || 0);
    const playbackTime = roundToTenth(
      Math.max(0, Number(markerCurrentTime) || 0),
    );

    let targetIndex = selectedMarkerIndex;
    if (targetIndex == null || !markerDraft[targetIndex]) {
      targetIndex = markerDraft.findIndex((m) => {
        const from = roundToTenth(Number(m.from_sec) || 0);
        const to = roundToTenth(Math.max(from, Number(m.to_sec) || from));
        return playbackTime >= from && playbackTime <= to;
      });
    }
    if (targetIndex == null || targetIndex < 0 || !markerDraft[targetIndex]) {
      targetIndex = 0;
    }

    const target = markerDraft[targetIndex];
    const targetFrom = roundToTenth(Number(target.from_sec) || 0);
    const targetTo = roundToTenth(
      Math.max(targetFrom, Number(target.to_sec) || targetFrom),
    );

    const snappedPlaybackTime = shouldSnapToSceneChanges
      ? findClosestSceneTime(sceneTimes, playbackTime, {
          min: targetFrom,
          max: targetTo,
        })
      : null;

    const staticFrom = roundToTenth(
      clamp(snappedPlaybackTime ?? playbackTime, targetFrom, targetTo),
    );
    const staticTo = roundToTenth(
      clamp(staticFrom + lengthSec, targetFrom, targetTo),
    );

    if (staticTo <= staticFrom) {
      toast.error(
        "Static marker length must fit inside the selected marker range",
      );
      return;
    }

    const pointAt = (t) =>
      getPointAtTime(markerRoutePoints, t, total) || [0, 0];
    const startPoint = pointAt(staticFrom);
    const endPoint = pointAt(staticTo);

    const pieces = [];

    if (staticFrom > targetFrom) {
      const beforeStart = pointAt(targetFrom);
      pieces.push({
        from_sec: targetFrom,
        to_sec: roundToTenth(staticFrom),
        start_lat: beforeStart[0],
        start_lng: beforeStart[1],
        end_lat: startPoint[0],
        end_lng: startPoint[1],
        radius_meters: null,
      });
    }

    pieces.push({
      from_sec: roundToTenth(staticFrom),
      to_sec: roundToTenth(staticTo),
      start_lat: startPoint[0],
      start_lng: startPoint[1],
      end_lat: startPoint[0],
      end_lng: startPoint[1],
      radius_meters: radiusMeters,
    });

    if (staticTo < targetTo) {
      const afterEnd = pointAt(targetTo);
      pieces.push({
        from_sec: roundToTenth(staticTo),
        to_sec: targetTo,
        start_lat: endPoint[0],
        start_lng: endPoint[1],
        end_lat: afterEnd[0],
        end_lng: afterEnd[1],
        radius_meters: null,
      });
    }

    const next = [...markerDraft];
    next.splice(targetIndex, 1, ...pieces);
    setMarkerDraft(next);
    const staticIndex = targetIndex + (staticFrom > targetFrom ? 1 : 0);
    setSelectedMarkerIndex(staticIndex);
    setMapPickMode({ type: "static", index: staticIndex });
    toast.success(
      `${shouldSnapToSceneChanges ? "Scene-snapped " : ""}static marker added (${lengthSec}s, ${radiusMeters}m radius) with route segments filling remaining time`,
    );
  };

  const handleMarkerMapPick = ({ lat, lng }) => {
    if (!mapPickMode) return;
    const pickedLat = Number(lat) || 0;
    const pickedLng = Number(lng) || 0;
    const snapped = closestGpxPoint(pickedLat, pickedLng);

    const next = [...markerDraft];

    if (mapPickMode.type === "split") {
      if (!snapped) return;
      const rightIdx = mapPickMode.rightIndex;
      const leftIdx = rightIdx - 1;
      if (leftIdx >= 0 && next[leftIdx]) {
        next[leftIdx] = {
          ...next[leftIdx],
          end_lat: snapped[0],
          end_lng: snapped[1],
        };
      }
      if (rightIdx >= 0 && next[rightIdx]) {
        next[rightIdx] = {
          ...next[rightIdx],
          start_lat: snapped[0],
          start_lng: snapped[1],
        };
        setSelectedMarkerIndex(rightIdx);
      }
      setMarkerDraft(next);
      setMapPickMode(null);
      toast.success("Split boundary snapped to nearest GPX point");
      return;
    }

    if (mapPickMode.type === "static") {
      const idx = mapPickMode.index;

      if (idx >= 0 && next[idx]) {
        next[idx] = {
          ...next[idx],
          start_lat: pickedLat,
          start_lng: pickedLng,
          end_lat: pickedLat,
          end_lng: pickedLng,
        };
        if (idx > 0 && next[idx - 1]) {
          next[idx - 1] = {
            ...next[idx - 1],
            end_lat: pickedLat,
            end_lng: pickedLng,
          };
        }
        if (idx < next.length - 1 && next[idx + 1]) {
          next[idx + 1] = {
            ...next[idx + 1],
            start_lat: pickedLat,
            start_lng: pickedLng,
          };
        }
        setSelectedMarkerIndex(idx);
      }
      setMarkerDraft(next);
      setMapPickMode(null);
      toast.success("Static marker location updated");
      return;
    }

    if (mapPickMode.type === "marker-start") {
      if (!snapped) return;
      const idx = mapPickMode.index;
      if (idx >= 0 && next[idx]) {
        const isStatic =
          next[idx].radius_meters !== null &&
          next[idx].radius_meters !== undefined;
        next[idx] = {
          ...next[idx],
          start_lat: snapped[0],
          start_lng: snapped[1],
          ...(isStatic
            ? {
                end_lat: snapped[0],
                end_lng: snapped[1],
              }
            : {}),
        };
        if (idx > 0 && next[idx - 1]) {
          next[idx - 1] = {
            ...next[idx - 1],
            end_lat: snapped[0],
            end_lng: snapped[1],
          };
        }
        setSelectedMarkerIndex(idx);
      }
      setMarkerDraft(next);
      setMapPickMode(null);
      toast.success("Marker start snapped to nearest GPX point");
      return;
    }

    if (mapPickMode.type === "marker-end") {
      if (!snapped) return;
      const idx = mapPickMode.index;
      if (idx >= 0 && next[idx]) {
        next[idx] = {
          ...next[idx],
          end_lat: snapped[0],
          end_lng: snapped[1],
        };
        if (idx < next.length - 1 && next[idx + 1]) {
          next[idx + 1] = {
            ...next[idx + 1],
            start_lat: snapped[0],
            start_lng: snapped[1],
          };
        }
        setSelectedMarkerIndex(idx);
      }
      setMarkerDraft(next);
      setMapPickMode(null);
      toast.success("Marker end snapped to nearest GPX point");
    }
  };

  const deleteMarkerAtIndex = (idx) => {
    if (idx == null || !markerDraft[idx]) return;
    if (markerDraft.length <= 1) {
      setMarkerDraft([
        buildDefaultMarker(video, routes, editingRoute, editingPlaylist),
      ]);
      setSelectedMarkerIndex(0);
      setMapPickMode(null);
      toast.success("Last marker replaced with the default marker");
      return;
    }

    const current = markerDraft[idx];
    const prev = idx > 0 ? markerDraft[idx - 1] : null;
    const next = idx < markerDraft.length - 1 ? markerDraft[idx + 1] : null;
    const list = [...markerDraft];

    if (prev && next) {
      list[idx - 1] = {
        ...prev,
        to_sec: next.from_sec,
        end_lat: next.start_lat,
        end_lng: next.start_lng,
      };
      list.splice(idx, 1);
      setSelectedMarkerIndex(Math.max(0, idx - 1));
    } else if (prev) {
      list[idx - 1] = {
        ...prev,
        to_sec: current.to_sec,
        end_lat: current.end_lat,
        end_lng: current.end_lng,
      };
      list.splice(idx, 1);
      setSelectedMarkerIndex(Math.max(0, idx - 1));
    } else if (next) {
      list[idx + 1] = {
        ...next,
        from_sec: current.from_sec,
        start_lat: current.start_lat,
        start_lng: current.start_lng,
      };
      list.splice(idx, 1);
      setSelectedMarkerIndex(0);
    }
    setMarkerDraft(list);
    setMapPickMode(null);
    toast.success("Marker removed and adjacent segments merged");
  };

  const resetMarkersToDefault = () => {
    const confirmed =
      typeof globalThis.confirm !== "function" ||
      globalThis.confirm(
        "Reset all markers to the default marker? This will discard your current marker edits.",
      );
    if (!confirmed) return;

    setMarkerDraft([
      buildDefaultMarker(video, routes, editingRoute, editingPlaylist),
    ]);
    setSelectedMarkerIndex(0);
    setMapPickMode(null);
    toast.success("Markers reset to the default marker");
  };

  const deleteSelectedMarker = () => {
    deleteMarkerAtIndex(selectedMarkerIndex);
  };

  const saveMarkers = async () => {
    if (!video?.id) return;
    try {
      const totalDuration = Math.max(
        0,
        Number(markerDuration) || Number(video?.duration) || 0,
      );
      const normalizedMarkers = interpolateAndFillMarkerRanges(
        markerDraft,
        totalDuration,
        markerRoutePoints,
      );
      const payload = {
        ...video,
        markers: normalizedMarkers,
      };
      if (typeof onSave === "function") {
        await onSave(payload);
      }
      toast.success("Markers updated");
      onSaved && onSaved();
      onClose && onClose(null);
    } catch (e) {
      toast.error("Failed to save markers");
    }
  };

  const isMapPickPending = !!mapPickMode;
  const mapPickTargetLabel =
    mapPickMode?.type === "split"
      ? "split boundary"
      : mapPickMode?.type === "marker-start"
        ? "marker start"
        : mapPickMode?.type === "marker-end"
          ? "marker end"
          : mapPickMode?.type === "static"
            ? "static marker"
            : "location";

  if (!video) return null;

  return (
    <Dialog open={!!video} onOpenChange={(o) => !o && onClose && onClose(null)}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto lg:overflow-hidden">
        <DialogHeader>
          <DialogTitle>Marker editor: {video?.title || "Video"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start lg:h-[calc(92vh-190px)] lg:overflow-hidden">
          <div className="lg:col-span-3 flex flex-col gap-4 lg:h-full lg:overflow-hidden">
            <div className="rounded-xl border border-[#E5E0D8] overflow-hidden bg-black h-[300px] lg:h-[38vh] shrink-0">
              <MediaPlayer
                ref={markerPlayerRef}
                src={video?.video_url || video?.video}
                poster={video?.thumbnail || video?.poster}
                onTimeUpdate={setMarkerCurrentTime}
                onDuration={setMarkerDuration}
              />
            </div>

            <div className="rounded-xl overflow-hidden border border-[#E5E0D8] h-[240px] lg:h-[30vh] shrink-0">
              <MapView
                gpxPoints={markerRoutePoints}
                markerSegment={
                  selectedMarkerIndex != null &&
                  markerDraft[selectedMarkerIndex]
                    ? {
                        start: [
                          Number(markerDraft[selectedMarkerIndex].start_lat) ||
                            0,
                          Number(markerDraft[selectedMarkerIndex].start_lng) ||
                            0,
                        ],
                        end: [
                          Number(markerDraft[selectedMarkerIndex].end_lat) || 0,
                          Number(markerDraft[selectedMarkerIndex].end_lng) || 0,
                        ],
                      }
                    : null
                }
                markerRadius={
                  selectedMarkerIndex != null &&
                  markerDraft[selectedMarkerIndex] &&
                  markerDraft[selectedMarkerIndex].radius_meters != null
                    ? Math.max(
                        0,
                        Number(
                          markerDraft[selectedMarkerIndex].radius_meters,
                        ) || 0,
                      )
                    : null
                }
                markerPosition={
                  selectedMarkerIndex != null &&
                  markerDraft[selectedMarkerIndex] && {
                    lat:
                      Number(markerDraft[selectedMarkerIndex].start_lat) || 0,
                    lng:
                      Number(markerDraft[selectedMarkerIndex].start_lng) || 0,
                  }
                }
                onMapClick={handleMarkerMapPick}
                highlightPosition={hoveredRoutePosition}
                height="100%"
                fit
              />
            </div>

            <ElevationProfile
              gpxPoints={markerRoutePoints}
              series={elevationSeries}
              sectionLines={markerSectionLines}
              duration={markerDuration || Number(video?.duration) || 0}
              currentTime={markerCurrentTime}
              highlightTime={hoveredRouteTime}
              onSeek={handleProfileSeek}
              onHoverTime={handleHoverTime}
            />
          </div>

          <div className="lg:col-span-2 flex flex-col space-y-2 lg:h-full lg:min-h-0">
            <div className="text-sm text-[#5C665F]">
              Current time: {markerCurrentTime.toFixed(1)}s /{" "}
              {(markerDuration || Number(video.duration) || 0).toFixed(1)}s
            </div>
            <div className="flex items-center justify-between rounded-xl border border-[#E5E0D8] bg-white px-3 py-2">
              <div>
                <div className="text-sm font-semibold text-[#1A1D1A]">
                  Sync selection to playback
                </div>
                <div className="text-xs text-[#5C665F]">
                  Keep selected marker locked to current video position.
                </div>
              </div>
              <Switch
                checked={isMarkerSelectionLocked}
                onCheckedChange={setIsMarkerSelectionLocked}
                aria-label="Toggle marker selection sync"
              />
            </div>

            <div className="flex items-center justify-between rounded-xl border border-[#E5E0D8] bg-white px-3 py-2">
              <div>
                <div className="text-sm font-semibold text-[#1A1D1A]">
                  Snap timing to scene changes
                </div>
                <div className="text-xs text-[#5C665F]">
                  {sceneTimes.length > 0
                    ? "Use nearest scene change time when splitting or adding markers at current time."
                    : "No scene changes available for this video."}
                </div>
              </div>
              <Switch
                checked={snapToSceneChanges}
                onCheckedChange={setSnapToSceneChanges}
                disabled={sceneTimes.length === 0}
                aria-label="Toggle scene change snapping"
              />
            </div>

            {mapPickMode && (
              <div className="rounded-xl border-2 border-[#C86B4E] bg-[#FFF3E8] px-3 py-2 shadow-sm">
                <div className="text-[10px] uppercase tracking-[0.2em] font-black text-[#C86B4E]">
                  Action Required
                </div>
                <div className="text-sm font-bold text-[#1A1D1A] mt-1">
                  Click the map now to place the {mapPickTargetLabel}.
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Static length (s)</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={staticMarkerLengthSec}
                  disabled={isMapPickPending}
                  onChange={(e) =>
                    setStaticMarkerLengthSec(
                      Math.max(0, parseFloat(e.target.value) || 0),
                    )
                  }
                />
              </div>
              <div>
                <Label>Static radius (m)</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={staticMarkerRadiusMeters}
                  disabled={isMapPickPending}
                  onChange={(e) =>
                    setStaticMarkerRadiusMeters(
                      Math.max(0, parseFloat(e.target.value) || 0),
                    )
                  }
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                disabled={isMapPickPending}
                onClick={splitSelectedMarkerAtCurrentTime}
              >
                Split at current time
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={isMapPickPending}
                onClick={addStaticMarkerAtCurrentTime}
              >
                Add static marker
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={isMapPickPending}
                onClick={deleteSelectedMarker}
                data-testid="delete-selected-marker-button"
              >
                Delete selected marker
              </Button>
            </div>

            <div className="flex-1 min-h-[240px] lg:min-h-0 overflow-y-auto pr-1 space-y-2 border border-[#E5E0D8] rounded-xl p-2 bg-[#FAF8F4]">
              {markerDraft.map((m, idx) => {
                const isSelected = idx === selectedMarkerIndex;
                const from = roundToTenth(Number(m.from_sec) || 0);
                const to = roundToTenth(
                  Math.max(from, Number(m.to_sec) || from),
                );
                const durationSec = roundToTenth(Math.max(0, to - from));
                const now = Number(markerCurrentTime) || 0;
                const isPlaying =
                  durationSec > 0
                    ? now >= from && now < to
                    : Math.abs(now - from) < 0.05;
                const isStatic =
                  m.radius_meters !== null && m.radius_meters !== undefined;
                return (
                  <div
                    key={`marker-editor-${idx}`}
                    className={`rounded-xl border p-3 ${isSelected ? "border-[#2C5545] bg-[#2C5545]/5" : "border-[#E5E0D8] bg-white"}`}
                    onClick={() => setSelectedMarkerIndex(idx)}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="text-xs font-semibold inline-flex items-center gap-2">
                        <span>Marker {idx + 1}</span>
                        {isPlaying && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#2C5545]/10 px-2 py-0.5 text-[10px] font-semibold text-[#2C5545]">
                            <SpinnerGap size={12} className="animate-spin" />
                            Playing
                          </span>
                        )}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={isMapPickPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteMarkerAtIndex(idx);
                        }}
                        data-testid={`delete-marker-${idx}`}
                      >
                        Delete
                      </Button>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label>Start (s)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={from}
                          disabled={isMapPickPending}
                          onChange={(e) => {
                            const next = [...markerDraft];
                            next[idx] = {
                              ...next[idx],
                              from_sec: roundToTenth(
                                parseFloat(e.target.value) || 0,
                              ),
                            };
                            setMarkerDraft(next);
                          }}
                          data-testid={`marker-start-${idx}`}
                        />
                      </div>
                      <div>
                        <Label>End (s)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={to}
                          disabled={isMapPickPending}
                          onChange={(e) => {
                            const next = [...markerDraft];
                            next[idx] = {
                              ...next[idx],
                              to_sec: roundToTenth(
                                parseFloat(e.target.value) || 0,
                              ),
                            };
                            setMarkerDraft(next);
                          }}
                          data-testid={`marker-end-${idx}`}
                        />
                      </div>
                      <div>
                        <Label>Duration (s)</Label>
                        <Input value={durationSec} readOnly />
                      </div>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={isMapPickPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          markerPlayerRef.current?.seekTo?.(from);
                          setMarkerCurrentTime(from);
                        }}
                      >
                        Jump to Start
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={isMapPickPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          markerPlayerRef.current?.seekTo?.(to);
                          setMarkerCurrentTime(to);
                        }}
                      >
                        Jump to End
                      </Button>
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={isMapPickPending}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMarkerIndex(idx);
                          setMapPickMode({ type: "marker-start", index: idx });
                        }}
                      >
                        Pick Start
                      </Button>

                      {isStatic ? (
                        <div>
                          <Label>Radius (m)</Label>
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            value={Number(m.radius_meters) || 0}
                            disabled={isMapPickPending}
                            onChange={(e) => {
                              const next = [...markerDraft];
                              next[idx] = {
                                ...next[idx],
                                radius_meters: Math.max(
                                  0,
                                  parseFloat(e.target.value) || 0,
                                ),
                              };
                              setMarkerDraft(next);
                            }}
                          />
                        </div>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={isMapPickPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedMarkerIndex(idx);
                            setMapPickMode({ type: "marker-end", index: idx });
                          }}
                        >
                          Pick End
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
              {markerDraft.length === 0 && (
                <div className="text-sm text-[#5C665F]">
                  No markers available.
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={resetMarkersToDefault}
            data-testid="reset-markers-button"
          >
            Reset
          </Button>
          <Button variant="outline" onClick={() => onClose && onClose(null)}>
            Cancel
          </Button>
          <Button
            disabled={isMapPickPending}
            onClick={saveMarkers}
            className="bg-[#2C5545] text-white hover:bg-[#18382E] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save markers
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
